import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";
import type { CommandItem, NavTreeSection } from "@novel-isr/ui";
import {
  FiActivity,
  FiBarChart2,
  FiBookOpen,
  FiCloud,
  FiCreditCard,
  FiDatabase,
  FiFileText,
  FiFlag,
  FiGrid,
  FiKey,
  FiLink,
  FiSearch,
  FiServer,
  FiShield,
  FiSliders,
  FiTerminal,
  FiUploadCloud,
  FiUser,
} from "react-icons/fi";

export type AdminRoutePage = LazyExoticComponent<ComponentType>;

export interface AdminRoute {
  path: string;
  Page: AdminRoutePage;
}

export type AdminCommandItem = CommandItem & { target?: string };

export const ROUTE_PAGES = {
  account: lazy(() =>
    import("./pages/account/AccountPage.tsx").then((module) => ({
      default: module.AccountPage,
    })),
  ),
  analytics: lazy(() =>
    import("./pages/analytics/AnalyticsPage.tsx").then((module) => ({
      default: module.AnalyticsPage,
    })),
  ),
  audit: lazy(() =>
    import("./pages/audit/AuditPage.tsx").then((module) => ({
      default: module.AuditPage,
    })),
  ),
  books: lazy(() =>
    import("./pages/books/BooksPage.tsx").then((module) => ({
      default: module.BooksPage,
    })),
  ),
  buildTokens: lazy(() =>
    import("./pages/build-tokens/BuildTokensPage.tsx").then((module) => ({
      default: module.BuildTokensPage,
    })),
  ),
  login: lazy(() =>
    import("./pages/auth/LoginPage.tsx").then((module) => ({
      default: module.LoginPage,
    })),
  ),
  dashboard: lazy(() =>
    import("./pages/dashboard/DashboardPage.tsx").then((module) => ({
      default: module.DashboardPage,
    })),
  ),
  deployments: lazy(() =>
    import("./pages/deployments/DeploymentsPage.tsx").then((module) => ({
      default: module.DeploymentsPage,
    })),
  ),
  i18n: lazy(() =>
    import("./pages/i18n/I18nPage.tsx").then((module) => ({
      default: module.I18nPage,
    })),
  ),
  mock: lazy(() =>
    import("./pages/mock/MockPage.tsx").then((module) => ({
      default: module.MockPage,
    })),
  ),
  seo: lazy(() =>
    import("./pages/seo/SeoPage.tsx").then((module) => ({
      default: module.SeoPage,
    })),
  ),
  environments: lazy(() =>
    import("./pages/operations/OperationsPages.tsx").then((module) => ({
      default: module.EnvironmentsPage,
    })),
  ),
  finance: lazy(() =>
    import("./pages/operations/OperationsPages.tsx").then((module) => ({
      default: module.FinancePage,
    })),
  ),
  flags: lazy(() =>
    import("./pages/operations/OperationsPages.tsx").then((module) => ({
      default: module.FlagsPage,
    })),
  ),
  experiments: lazy(() =>
    import("./pages/experiments/ExperimentsPage.tsx").then((module) => ({
      default: module.ExperimentsPage,
    })),
  ),
  insights: lazy(() =>
    import("./pages/operations/OperationsPages.tsx").then((module) => ({
      default: module.InsightsPage,
    })),
  ),
  integrations: lazy(() =>
    import("./pages/integrations/IntegrationsPage.tsx").then((module) => ({
      default: module.IntegrationsPage,
    })),
  ),
  moderation: lazy(() =>
    import("./pages/operations/OperationsPages.tsx").then((module) => ({
      default: module.ModerationPage,
    })),
  ),
  notFound: lazy(() =>
    import("./pages/operations/OperationsPages.tsx").then((module) => ({
      default: module.NotFoundPage,
    })),
  ),
  pipelines: lazy(() =>
    import("./pages/operations/OperationsPages.tsx").then((module) => ({
      default: module.PipelinesPage,
    })),
  ),
  users: lazy(() =>
    import("./pages/users/UsersPage.tsx").then((module) => ({
      default: module.UsersPage,
    })),
  ),
  sessions: lazy(() =>
    import("./pages/operations/SessionsPage.tsx").then((module) => ({
      default: module.SessionsPage,
    })),
  ),
} satisfies Record<string, AdminRoutePage>;

export const ADMIN_ROUTES = [
  { path: "/", Page: ROUTE_PAGES.dashboard },
  { path: "/books", Page: ROUTE_PAGES.books },
  { path: "/i18n", Page: ROUTE_PAGES.i18n },
  { path: "/seo", Page: ROUTE_PAGES.seo },
  { path: "/mock", Page: ROUTE_PAGES.mock },
  { path: "/deployments", Page: ROUTE_PAGES.deployments },
  { path: "/deployments/environments", Page: ROUTE_PAGES.environments },
  { path: "/deployments/pipelines", Page: ROUTE_PAGES.pipelines },
  { path: "/account", Page: ROUTE_PAGES.account },
  { path: "/users", Page: ROUTE_PAGES.users },
  { path: "/build-tokens", Page: ROUTE_PAGES.buildTokens },
  { path: "/audit", Page: ROUTE_PAGES.audit },
  { path: "/flags", Page: ROUTE_PAGES.flags },
  { path: "/experiments", Page: ROUTE_PAGES.experiments },
  { path: "/finance", Page: ROUTE_PAGES.finance },
  { path: "/analytics", Page: ROUTE_PAGES.analytics },
  { path: "/analytics/insights", Page: ROUTE_PAGES.insights },
  { path: "/integrations", Page: ROUTE_PAGES.integrations },
  { path: "/moderation", Page: ROUTE_PAGES.moderation },
  // 在线用户（live presence + revoke）—— 真实日志检索走 Loki / Datadog，不在 admin
  { path: "/operations", Page: ROUTE_PAGES.sessions },
  { path: "*", Page: ROUTE_PAGES.notFound },
] satisfies AdminRoute[];

export const NAV_SECTIONS = [
  {
    id: "console",
    title: "控制台",
    items: [
      {
        id: "overview",
        href: "/",
        label: "总览",
        description: "运行态",
        icon: <FiGrid />,
      },
    ],
  },
  {
    id: "identity",
    title: "身份与访问",
    items: [
      {
        id: "identity-domain",
        label: "账号与权限",
        description: "我的账号 / 用户管理",
        icon: <FiUser />,
        children: [
          {
            id: "account",
            href: "/account",
            label: "我的账号",
            description: "权限 / 会话",
            icon: <FiUser />,
          },
          {
            id: "users",
            href: "/users",
            label: "用户管理",
            description: "管理员专用",
            icon: <FiUser />,
          },
          {
            id: "build-tokens",
            href: "/build-tokens",
            label: "构建令牌",
            description: "CI / build snapshot",
            icon: <FiKey />,
          },
        ],
      },
    ],
  },
  {
    id: "content",
    title: "内容运营",
    items: [
      {
        id: "content-catalog",
        label: "业务数据",
        description: "书库 / 书单 / 作者",
        icon: <FiBookOpen />,
        children: [
          {
            id: "books",
            href: "/books",
            label: "书库管理",
            description: "录入小说元数据",
            icon: <FiBookOpen />,
          },
        ],
      },
    ],
  },
  {
    id: "runtime",
    title: "运营配置",
    items: [
      {
        id: "runtime-config",
        label: "配置管理",
        description: "文案 / SEO / Mock",
        icon: <FiSliders />,
        children: [
          {
            id: "i18n",
            href: "/i18n",
            label: "多语言文案",
            description: "配置下发",
            icon: <FiFileText />,
          },
          {
            id: "seo",
            href: "/seo",
            label: "SEO 配置",
            description: "搜索增长",
            icon: <FiSearch />,
          },
          {
            id: "mock",
            href: "/mock",
            label: "接口模拟",
            description: "服务编排",
            icon: <FiServer />,
          },
          {
            id: "flags",
            href: "/flags",
            label: "灰度开关",
            description: "发布治理",
            icon: <FiFlag />,
          },
          {
            id: "experiments",
            href: "/experiments",
            label: "A/B 实验",
            description: "曝光 / uplift",
            icon: <FiActivity />,
          },
        ],
      },
    ],
  },
  {
    id: "insight",
    title: "数据增长",
    items: [
      {
        id: "insight-domain",
        label: "数据与日志",
        description: "埋点 / 性能 / 日志",
        icon: <FiBarChart2 />,
        children: [
          {
            id: "analytics",
            href: "/analytics",
            label: "埋点性能",
            description: "Web Vitals",
            icon: <FiActivity />,
          },
          {
            id: "analytics-insights",
            href: "/analytics/insights",
            label: "数据分析",
            description: "访问漏斗",
            icon: <FiBarChart2 />,
          },
          {
            id: "live-sessions",
            href: "/operations",
            label: "在线用户",
            description: "实时 presence + revoke",
            icon: <FiActivity />,
          },
        ],
      },
    ],
  },
  {
    id: "delivery",
    title: "部署平台",
    items: [
      {
        id: "delivery-control",
        label: "发布交付",
        description: "环境 / 流水线 / 回滚",
        icon: <FiUploadCloud />,
        children: [
          {
            id: "deployments",
            href: "/deployments",
            label: "发布总览",
            description: "部署台账",
            icon: <FiUploadCloud />,
          },
          {
            id: "environments",
            href: "/deployments/environments",
            label: "环境管理",
            description: "dev/stage/prod",
            icon: <FiCloud />,
          },
          {
            id: "pipelines",
            href: "/deployments/pipelines",
            label: "流水线",
            description: "CI/CD 执行入口",
            icon: <FiTerminal />,
          },
        ],
      },
    ],
  },
  {
    id: "governance",
    title: "财务与治理",
    items: [
      {
        id: "governance-domain",
        label: "治理后台",
        description: "财务 / 审核 / 接入",
        icon: <FiShield />,
        children: [
          {
            id: "finance",
            href: "/finance",
            label: "财务结算",
            description: "收入对账",
            icon: <FiCreditCard />,
          },
          {
            id: "moderation",
            href: "/moderation",
            label: "内容审核",
            description: "风险控制",
            icon: <FiShield />,
          },
          {
            id: "integrations",
            href: "/integrations",
            label: "外部接入",
            description: "SSO / 支付 / 存储",
            icon: <FiLink />,
          },
          {
            id: "audit",
            href: "/audit",
            label: "审计日志",
            description: "变更追踪",
            icon: <FiDatabase />,
          },
        ],
      },
    ],
  },
] satisfies NavTreeSection[];

/** 仅管理员（拥有 `*` scope）可见的导航项 id 集合。
 *  AdminShell 渲染 nav 前会按当前用户 permissions 过滤掉这里列的项。 */
export const ADMIN_ONLY_NAV_ITEM_IDS = new Set<string>([
  "users",
  "build-tokens",
  "books",
]);

export const COMMAND_ITEMS = [
  ...flattenNavItems(NAV_SECTIONS).map(({ section, item }) => ({
    id: `nav:${item.href}`,
    group: section.title,
    title: item.label,
    description: item.description,
    target: item.href,
    keywords: `${section.title ?? ""} ${item.label} ${item.description ?? ""} ${item.href}`,
  })),
  {
    id: "i18n:home",
    group: "文案 Key",
    title: "首页文案",
    description: "home.hero / home.rankings / home.featured",
    target: "/i18n",
    keywords: "home hero rankings featured 首页 榜单 推荐 文案 i18n",
  },
  {
    id: "i18n:navigation",
    group: "文案 Key",
    title: "导航与页脚文案",
    description: "nav.* / search.* / footer.*",
    target: "/i18n",
    keywords: "nav search footer navigation 搜索 导航 页脚",
  },
  {
    id: "seo:content",
    group: "SEO",
    title: "内容页 SEO",
    description: "/reviews /groups /topics /feed",
    target: "/seo",
    keywords: "reviews groups topics feed seo 书评 小组 话题 动态",
  },
  {
    id: "mock:community",
    group: "Mock",
    title: "社区接口模拟",
    description: "/api/reviews /api/groups /api/topics /api/feed",
    target: "/mock",
    keywords: "mock api reviews groups topics feed 接口模拟 社区",
  },
  {
    id: "deployments:main",
    group: "部署",
    title: "部署平台接入",
    description: "配置发布、灰度、回滚和审计的后续接入入口",
    target: "/deployments",
    keywords: "deploy release publish rollback audit 部署 发布 回滚",
  },
  {
    id: "account:profile",
    group: "账号",
    title: "账号与权限",
    description: "当前登录身份、权限范围和会话状态",
    target: "/account",
    keywords: "account profile role permission 用户 权限 账号",
  },
  {
    id: "audit:logs",
    group: "审计",
    title: "审计日志",
    description: "配置变更、登录和发布动作的追踪入口",
    target: "/audit",
    keywords: "audit logs event 审计 日志 变更",
  },
  {
    id: "analytics:vitals",
    group: "埋点",
    title: "页面性能与埋点",
    description: "Web Vitals、错误率、漏斗和部署影响分析",
    target: "/analytics",
    keywords:
      "analytics web vitals performance funnel error rum 埋点 性能 漏斗",
  },
] satisfies AdminCommandItem[];

function flattenNavItems(sections: readonly NavTreeSection[]) {
  const leaves: Array<{
    section: NavTreeSection;
    item: NavTreeSection["items"][number];
  }> = [];
  for (const section of sections) {
    collectNavLeaves(section, section.items, leaves);
  }
  return leaves.filter(({ item }) => Boolean(item.href));
}

function collectNavLeaves(
  section: NavTreeSection,
  items: readonly NavTreeSection["items"][number][],
  leaves: Array<{
    section: NavTreeSection;
    item: NavTreeSection["items"][number];
  }>,
) {
  for (const item of items) {
    leaves.push({ section, item });
    if (item.children) collectNavLeaves(section, item.children, leaves);
  }
}
